import { useEffect } from 'react';
import { deleteCookie, hasCookie } from 'cookies-next';

import { EMPTY_STR, HOME, LOGIN_SESSION_COOKIE, UNDEFINED } from '@/utils/constants/common';

const WHOSE_WATCHING_PATH = '/user-profile/whos-watching/';
const PROFILE_PICKER_BOOT_RETRY_INTERVAL_MS = 100;
const PROFILE_PICKER_BOOT_RETRY_MAX_ATTEMPTS = 50;

type MfeUserObjectForProfileBoot = {
    userStatus?: { description?: string | null };
};

/** True when a post-login boot redirect may still become eligible (cookie set, not on picker). */
export function shouldPollProfilePickerOnBootRedirect(): boolean {
    if (typeof window === UNDEFINED) {
        return false;
    }

    const pathname = window.location?.pathname ?? EMPTY_STR;

    return !pathname.includes(WHOSE_WATCHING_PATH) && hasCookie(LOGIN_SESSION_COOKIE);
}

/**
 * Redirect subscribers with UP_NEW_SESSION to Who's Watching after login.
 * Exported for unit tests; AppShell calls this via useProfilePickerOnBoot.
 */
export function attemptProfilePickerOnBootRedirect(): boolean {
    if (typeof window === UNDEFINED || !window.isMfe) {
        return false;
    }

    const userObject = window.CBS?.Registry?.mfeUserObject ?? null;
    const shouldBootWhosWatching = window.CBS?.Registry?.shouldBootWhosWatching ?? false;
    const cookie = hasCookie(LOGIN_SESSION_COOKIE);

    const pathname = window.location?.pathname ?? EMPTY_STR;

    if (
        !shouldBootWhosWatching ||
        !cookie ||
        pathname.includes(WHOSE_WATCHING_PATH) ||
        (userObject as MfeUserObjectForProfileBoot)?.userStatus?.description?.toLowerCase() !==
            'subscriber'
    ) {
        return false;
    }

    const destinationURL = encodeURI(pathname.trim().length > 0 ? pathname : HOME);
    window.location.assign(`${WHOSE_WATCHING_PATH}?destinationURL=${destinationURL}`);
    return true;
}

/**
 * After login, subscribers with UP_NEW_SESSION land on home and should be sent to
 * Who's Watching. Runs in AppShell so it applies on home, not only user-profiles MFE.
 */
const useProfilePickerOnBoot = () => {
    useEffect(() => {
        if (attemptProfilePickerOnBootRedirect()) {
            return;
        }

        if (!shouldPollProfilePickerOnBootRedirect()) {
            return;
        }

        let attempts = 0;
        const intervalId = window.setInterval(() => {
            attempts += 1;

            if (
                attemptProfilePickerOnBootRedirect() ||
                attempts >= PROFILE_PICKER_BOOT_RETRY_MAX_ATTEMPTS
            ) {
                window.clearInterval(intervalId);
            }
        }, PROFILE_PICKER_BOOT_RETRY_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);
};

export function clearProfilePickerBootCookie(): boolean {
    if (!hasCookie(LOGIN_SESSION_COOKIE)) {
        return false;
    }

    deleteCookie(LOGIN_SESSION_COOKIE);
    return true;
}

export default useProfilePickerOnBoot;
